import { z } from "zod";

/**
 * Should contain all active Posthog events, used to capture user behavior.
 * Format: <action>_<category>_<object>
 * - action: verb in past tense to describe what happened, e.g. clicked, viewed, logged, updated, created, deleted...
 * - category: context the event took place in, e.g. signup, block, project, ...
 * - object (optional): noun that refers to the component or location the event took, e.g. continue_button, payment_link, ...
 */
export enum EventName {
  // Common
  ClickedCopyToClipboardButton = "clicked_copy_to_clipboard_button",

  // Navigation events
  ClickedNavAddProjectButton = "clicked_nav_add_project_button",
  // Auth events
  ClickedSignupContinueButton = "clicked_signup_continue_button",
  ClickedSignupButton = "clicked_signup_button",
  ClickedSignupGoogleButton = "clicked_signup_google_button",
  ClickedLoginButton = "clicked_login_button",
  ClickedLoginGoogleButton = "clicked_login_google_button",
  ClickedLoginMagicLinkButton = "clicked_login_magic_link_button",
  ClickedLoginSendMagicLinkButton = "clicked_login_send_magic_link_button",
  ClickedLoginResetPasswordButton = "clicked_login_reset_password_button",
  ClickedUpdatePasswordUpdateButton = "clicked_update_password_update_button",
  ClickedResetPasswordResetButton = "clicked_reset_password_reset_button",
  ClickedConfirmActionButton = "clicked_confirm_action_button",
  LoggedIn = "logged_in",
  LoggedOut = "logged_out",
  // Subscription events
  ClickedSubscriptionCancelOption = "clicked_subscription_cancel_option",
  ClickedSubscriptionUpdateBillingDetailsOption = "clicked_subscription_update_billing_details_option",
  ClickedSubscriptionSelectPlanButton = "clicked_subscription_select_plan_button",
  ViewedSubscriptionPaymentPage = "viewed_subscription_payment_page",
  ViewedSubscriptionRenewalModal = "viewed_subscription_renewal_modal",
  ClickedSubscriptionRenewalButton = "clicked_subscription_renewal_button",
  CreatedCustomer = "created_customer",
  CompletedSubscriptionCheckoutSession = "completed_subscription_checkout_session",
  CreatedSubscription = "created_subscription",
  RenewedSubscription = "renewed_subscription",
  CanceledSubscription = "canceled_subscription",
  SetSubscriptionCancelation = "set_subscription_cancelation",
  UnsetSubscriptionCancelation = "unset_subscription_cancelation",
  PausedSubscription = "paused_subscription",
  ResumedSubscription = "resumed_subscription",
  ClickedSubscriptionUpdateItemButton = "clicked_subscription_update_item_button",
  UpdatedSubscriptionItem = "updated_subscription_item",
  ConvertedSubscriptionTrial = "converted_subscription_trial",
  // Fact events
  CreatedBlock = "created_block",
  UpdatedBlock = "updated_block",
  ClickedBlockHistoryButton = "clicked_block_history_button",
  RevertedBlockHistory = "reverted_block_version",
  ClickedBlockRegenerateOptionsButton = "clicked_block_regenerate_options_button",
  ClickedBlockRegenerateCustomMenuItem = "clicked_block_regenerate_custom_menu_item",
  RegeneratedBlock = "regenerated_block",
  ClickedEditStacksButton = "clicked_edit_stacks_button",
  ClickedExploreStacksButton = "clicked_explore_stacks_button",
  // Asset events
  ClickedAssetSuggestion = "clicked_asset_suggestion",
  CreatedAsset = "created_asset",
  UpdatedAssetResult = "updated_asset_result",
  UpdatedAssetInstructions = "updated_asset_instructions",
  DeletedAsset = "deleted_asset",
  CreatedAssetVersion = "created_asset_version",
  UpdatedAssetVersion = "updated_asset_version",
  UpdatedActiveAssetVersion = "updated_active_asset_version",
  // Settings events
  ToggledFeature = "toggled_feature",
  ClickedSettingsAddProjectButton = "clicked_settings_add_project_button",
  ClickedSettingsAddUserButton = "clicked_settings_add_user_button",
  ClickedSettingsInviteUserButton = "clicked_settings_invite_user_button",
  ClickedSettingsDeleteUserMenuItem = "clicked_settings_delete_user_menu_item",
  ClickedSettingsUpdateUserRoleMenuItem = "clicked_settings_update_user_role_menu_item",
  ClickedSettingsJoinReferralButton = "clicked_settings_join_referral_button",
  ClickedSettingsAddApiKeyButton = "clicked_settings_add_api_key_button",
  ClickedSettingsConfirmResetApiKeyButton = "clicked_settings_confirm_reset_api_key_button",
  ClickedSettingsConfirmDeleteApiKeyButton = "clicked_settings_confirm_delete_api_key_button",
  ClickedSettingsTransferButton = "clicked_settings_transfer_button",
  ClickedSettingsConfirmTransferButton = "clicked_settings_confirm_transfer_button",
  JoinedBeta = "joined_beta",
  LeftBeta = "left_beta",
  // Project events
  CreatedProject = "created_project",
  UpdatedProject = "updated_project",
  ClickedProjectGraphButton = "clicked_project_graph_button",
  // User events
  UpdatedUserProfile = "updated_user_profile",
  UpdatedUserRole = "updated_user_role",
  CreatedUser = "created_user",
  DeletedUser = "deleted_user",
  CreatedBotIdentity = "created_bot_identity",
  // Onboarding events
  ClickedOnboardingLearnButton = "clicked_onboarding_learn_button",
  ClosedOnboardingLearnContent = "closed_onboarding_learn_content",
  // Onboarding form events
  ClickedOnboardingFormSubmitButton = "clicked_onboarding_form_submit_button",
  ClickedOnboardingFormStartUsingNoanButton = "clicked_onboarding_form_start_using_noan_button",
  ClickedOnboardingFormHostLink = "clicked_onboarding_form_host_link",
  // Assistant events
  CreatedChatThread = "created_chat_thread",
  UpdatedChatThreadTitle = "updated_chat_thread_title",
  UpdatedChatThreadMessages = "updated_chat_thread_messages",
  ClickedChatThreadSendMessageToAssetButton = "clicked_chat_thread_send_message_to_asset_button",
  ClickedChatThreadCopyMessageButton = "clicked_chat_thread_copy_message_button",
  ClickedListRecentChatThreadsButton = "clicked_list_recent_chat_threads_button",
  ClickedNewChatThreadButton = "clicked_new_chat_thread_button",
  ClickedDocumentUploadFileButton = "clicked_document_upload_file_button",
  ClosedDocumentUploadFileModal = "closed_document_upload_file_modal",
  // Task events
  ClickedTaskAddButton = "clicked_task_add_button",
  CreatedTask = "created_task",
  UpdatedTask = "updated_task",
  DeletedTask = "deleted_task",
  UpdatedTaskAssignees = "updated_task_assignees",
  UpdatedTaskTags = "updated_task_tags",
  UpdatedTaskContacts = "updated_task_contacts",
  // Connection events
  CreatedConnection = "created_connection",
  UpdatedConnection = "updated_connection",
  DeletedConnection = "deleted_connection",
  ClickedViewEmailsButton = "clicked_view_emails_button",
  // Note events
  CreatedNote = "created_note",
  UpdatedNote = "updated_note",
  DeletedNote = "deleted_note",
  // Favorite
  CreatedFavorite = "created_favorite",
  DeletedFavorite = "deleted_favorite",
  // Contact events
  CreatedContact = "created_contact",
  UpdatedContact = "updated_contact",
  DeletedContact = "deleted_contact",
  UpdatedContactTasks = "updated_contact_tasks",
  // Company events
  CreatedCompany = "created_company",
  UpdatedCompany = "updated_company",
  DeletedCompany = "deleted_company",
  // Tag events
  CreatedTag = "created_tag",
  UpdatedTag = "updated_tag",
  DeletedTag = "deleted_tag",
  // Recommendation events
  GeneratedSuggestionPrompts = "generated_suggestion_prompts",
  ClickedSuggestionPrompt = "clicked_suggestion_prompt",
  // Bookmark events
  ClickedBookmarkSaveButton = "clicked_bookmark_save_button",
  ClickedBookmarkUpdateButton = "clicked_bookmark_update_button",
  ClickedBookmarkDeleteButton = "clicked_bookmark_delete_button",
  ClickedSavedBookmarkButton = "clicked_saved_bookmark_button",
  ClickedSavedBookmarkOption = "clicked_saved_bookmark_option",
  ClickedSavedBookmarkDeleteButton = "clicked_saved_bookmark_delete_button",
  // Personal API Key events
  CreatedPersonalApiKey = "created_personal_api_key",
  UpdatedPersonalApiKey = "updated_personal_api_key",
  RevokedPersonalApiKey = "revoked_personal_api_key",
  RotatedPersonalApiKey = "rotated_personal_api_key",
  // Agents events
  ClickedAgentsVoiceWaitlistButton = "clicked_agents_voice_waitlist_button",
}

export enum SubscriptionType {
  None = "None",
  Trial = "Trial",
  Paid = "Paid",
}

export enum EventSource {
  Posthog = "posthog",
}

export const allEventSources = Object.values(EventSource);

const EventPropertiesSchema = z.record(z.string(), z.unknown());

export const TrackEventPayloadSchema = z.object({
  eventName: z.enum(EventName),
  properties: EventPropertiesSchema,
  sources: z.array(z.enum(EventSource)).optional(),
});
export type TrackEventPayload = z.infer<typeof TrackEventPayloadSchema>;
